<!--
  REV-21363 — the shared upload modal (Pure View).

  Selector contract: tests bind to `data-testid` and `data-*` markers, never to styling classes and
  never to translated prose as an anchor. See e2e-lab/upload-modal.lab.spec.ts.

  ONE component for BOTH entry points (ADR-0009 §11) — the hub's `Upload` and the account area's
  upload control. The only difference is `[multiple]`.

  Hand-rolled rather than `ham-modal`: that component moves itself into `#global-modal-placeholder`
  on init, which does not exist outside the app shell (`selectRootElement` throws when it is
  missing). A pure view must render inside its own subtree — same reason `document-detail`
  hand-rolled its confirmation dialog.

  ⚠️ The prototype's subline says "PDF oder Bild · mehrere gleichzeitig möglich". ADR-0009 §12
  narrows the ingest to PDF, so the copy says PDF — a surface must not promise what the server
  answers with 415.
-->
<div
    class="um"
    data-testid="upload-modal"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'abcp_upload_modal_title' | i18next"
>
    <div class="um-card">
        <div class="um-head">
            <h3 class="um-head__title" data-testid="upload-modal-title">
                <lucide-icon name="upload" [size]="16" aria-hidden="true"></lucide-icon>
                {{ 'abcp_upload_modal_title' | i18next }}
            </h3>
            <ham-button
                class="um-head__close"
                data-testid="upload-modal-close"
                variant="transparent"
                size="xs"
                icon="assets/hamIcons/x.svg"
                [hideText]="true"
                (buttonClick)="onCancel()"
            ></ham-button>
        </div>

        <div class="um-body">
            <!-- Die Dropzone bleibt in JEDEM Zustand stehen: nach einer Auswahl will man weitere
                 Dateien hinzufügen können, ohne erst etwas wegzuräumen. -->
            <div
                class="um-dropzone"
                data-testid="upload-modal-dropzone"
                [attr.data-dragover]="isDragOver ? 'true' : null"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)"
            >
                <lucide-icon name="upload" [size]="28" aria-hidden="true"></lucide-icon>
                <div class="um-dropzone__title">{{ 'abcp_upload_modal_dropzone' | i18next }}</div>
                <div class="um-dropzone__hint" data-testid="upload-modal-hint">
                    {{ 'abcp_upload_modal_hint' | i18next: { max: formatSize(maxSizeBytes) } }}
                </div>

                <!-- Das Feld ist versteckt, aber ein echtes `input[type=file]`: der Dateidialog
                     lässt sich nicht nachbauen, und Playwright spricht es direkt an. -->
                <input
                    class="um-input"
                    type="file"
                    data-testid="upload-modal-input"
                    [accept]="accept"
                    [attr.multiple]="multiple ? '' : null"
                    (change)="onFilesPicked($any($event.target))"
                    #fileField
                />
                <ham-button
                    data-testid="upload-modal-pick"
                    variant="white"
                    size="sm"
                    (buttonClick)="fileField.click()"
                >
                    {{ 'abcp_upload_modal_pick' | i18next }}
                </ham-button>
            </div>

            <div class="um-list" *ngIf="files.length">
                <div class="um-list__label">{{ 'abcp_upload_modal_selected' | i18next }}</div>
                <div class="um-row" data-testid="upload-modal-file" *ngFor="let file of files; let i = index">
                    <lucide-icon class="um-row__icon" name="file-text" [size]="14" aria-hidden="true"></lucide-icon>
                    <span
                        class="um-row__name"
                        [hamTooltip]="file.name"
                        tooltipClass="abcp"
                        [tooltipOnlyWhenTruncated]="true"
                        >{{ file.name }}</span
                    >
                    <span class="um-row__size">{{ formatSize(file.size) }}</span>
                    <ham-button
                        class="um-row__remove"
                        data-testid="upload-modal-remove"
                        variant="transparent"
                        size="toolbar"
                        icon="assets/hamIcons/x.svg"
                        [hideText]="true"
                        (buttonClick)="onRemove(i)"
                    ></ham-button>
                </div>
            </div>

            <!-- ABGELEHNT — gezeigt MIT Grund und aus der Emission ausgeschlossen, während die
                 akzeptierten Dateien ausgewählt bleiben. Die ganze Auswahl zu verwerfen würde den
                 Nutzer Arbeit wiederholen lassen, die die Komponente schon richtig gemacht hat. -->
            <div class="um-list um-list--rejected" *ngIf="rejected.length">
                <div
                    class="um-row um-row--rejected"
                    data-testid="upload-modal-rejected"
                    *ngFor="let item of rejected"
                    [attr.data-reason]="item.reason"
                >
                    <lucide-icon class="um-row__icon" name="x" [size]="14" aria-hidden="true"></lucide-icon>
                    <span
                        class="um-row__name"
                        [hamTooltip]="item.filename"
                        tooltipClass="abcp"
                        [tooltipOnlyWhenTruncated]="true"
                        >{{ item.filename }}</span
                    >
                    <span class="um-row__reason">
                        {{
                            (item.reason === 'type'
                                ? 'abcp_upload_modal_rejected_type'
                                : 'abcp_upload_modal_rejected_size'
                            ) | i18next: { max: formatSize(maxSizeBytes) }
                        }}
                    </span>
                </div>
            </div>

            <!-- TEILFEHLER — vom Container zurückgemeldet. Anders als eine lokale Ablehnung war
                 diese Datei bereits unterwegs (ADR-0009 §11). -->
            <div class="um-list um-list--failed" *ngIf="failures.length">
                <div class="um-list__label">{{ 'abcp_upload_modal_failures' | i18next }}</div>
                <div class="um-row um-row--failed" data-testid="upload-modal-failure" *ngFor="let failure of failures">
                    <lucide-icon class="um-row__icon" name="x" [size]="14" aria-hidden="true"></lucide-icon>
                    <span
                        class="um-row__name"
                        [hamTooltip]="failure.filename"
                        tooltipClass="abcp"
                        [tooltipOnlyWhenTruncated]="true"
                        >{{ failure.filename }}</span
                    >
                    <span class="um-row__reason">{{ failure.message }}</span>
                </div>
            </div>

            <div class="um-progress" data-testid="upload-modal-progress" *ngIf="pending">
                <div class="um-progress__bar" aria-hidden="true"></div>
                {{ 'abcp_upload_modal_progress' | i18next }}
            </div>
        </div>

        <div class="um-foot">
            <ham-button data-testid="upload-modal-cancel" variant="white" (buttonClick)="onCancel()">
                {{ 'abcp_upload_modal_cancel' | i18next }}
            </ham-button>
            <ham-button
                data-testid="upload-modal-submit"
                variant="primary"
                icon="assets/hamIcons/upload.svg"
                [disabled]="isSubmitDisabled"
                (buttonClick)="onSubmit()"
            >
                {{ 'abcp_upload_modal_submit' | i18next }}
            </ham-button>
        </div>
    </div>
</div>
